// src/context/AuthContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
  useMemo,
} from "react";

import { flushSync } from "react-dom";
import { onAuthStateChanged, getIdToken, signOut } from "firebase/auth";
import { getFirebaseAuth } from "@/firebase";
import { useBranding } from "@/context/BrandingContext";
import { useInRouterContext, type NavigateFunction } from "react-router-dom";
import { apiJson, tabAccountFromPath } from "@/api/client";
import {
  reportSessionAccount,
  reportSessionNotice,
  setSessionAccountFromAuth,
} from "@/api/sessionAccount";
import { postSessionEvent, subscribeSessionEvents } from "@/api/sessionChannel";
import { NavigatorBridge } from "@/routing/NavigatorBridge";
import { sectionRoot } from "@/routing/sectionRoot";
import { confirmDiscardUnsaved } from "@/routing/unsavedChanges";
import { stripTenantPrefix } from "@/routing/tenantPaths";

import { AuthUser, AuthContextType, AuthError } from "@/types/auth";
import { readMovedDestination } from "@/types/authError";
import type { Capability, PartnerAccountLink } from "@verity/shared-types";
import { setAccountScope } from "@/utils/segmentCache";

const API_BASE = import.meta.env.VITE_API_URL;

// Access token lifetime minus 5 min buffer → proactive refresh at ~55 min
const PROACTIVE_REFRESH_MS = 55 * 60 * 1000;

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  loading: true,
  switching: false,
  setUser: () => {},
  refreshUser: async () => {},
  switchAccount: async () => false,
  logout: async () => {},
  authError: null,
  clearAuthError: () => {},
  reportAuthError: () => {},
  activeAccount: null,
  allAccounts: [],
  capabilities: [],
  hasCapability: () => false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // A switch in progress. Deliberately NOT `loading`: `loading` makes AppRoutes
  // render only a loader, which unmounts every page and its unsaved input.
  const [switching, setSwitching] = useState(false);
  const inRouter = useInRouterContext();
  const navigateRef = useRef<NavigateFunction | null>(null);
  const userRef = useRef<AuthUser | null>(null);

  // Every auth response is also the tab's best knowledge of the session
  // account (ADR-0302), recorded synchronously for the mismatch gate and the
  // navigation wrappers.
  const setUser = useCallback((next: AuthUser | null) => {
    userRef.current = next;
    setSessionAccountFromAuth(next?.activeAccount?.id ?? null);
    setUserState(next);
  }, []);
  // Why a sign-in attempt failed. Sticky until an explicit sign-in action or a
  // successful sign-in — see reportAuthError / clearAuthError below.
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Per-partner Firebase (F2): Firebase is initialised lazily by BrandingProvider
  // once the partner resolves. Gate the auth-state listener on this so it attaches
  // to the correct (partner) project and never fires against an uninitialised SDK.
  const { firebaseReady } = useBranding();

  // Dedup /auth/me calls
  const inflightMe = useRef<Promise<AuthUser | null> | null>(null);

  // Keep the segment cache namespaced to the active account at all times — covers
  // initial load, token refresh, and logout. Account switch sets this synchronously
  // before setUser (see switchAccount); this is the backstop for every other path
  // that mutates `user`. Cross-tenant isolation guard for the 8 h-TTL segment cache.
  useEffect(() => {
    setAccountScope(user?.activeAccount?.id ?? "");
  }, [user]);

  /**
   * Record why sign-in failed — FIRST failure of an attempt wins.
   *
   * Load-bearing. Both the sign-out we fire on a 401/403 and StrictMode's
   * double-mount (main.tsx) run the auth listener again for the same attempt.
   * Run #2 finds a Firebase user that is being (or has been) signed out, its
   * getIdToken throws, and it lands in the listener's catch. Without this
   * guard that second pass would overwrite "we couldn't sign you in with this
   * account" with a generic connection message — the user would again be told
   * nothing useful, which is the exact bug this closes.
   */
  const reportAuthError = useCallback((error: AuthError) => {
    setAuthError((prev) => {
      if (!prev) return error;
      // One narrow exception to first-wins: a report that carries a moved
      // DESTINATION upgrades one that does not. MagicLinkCallback runs its own
      // /auth/firebase exchange alongside this listener, and whichever loses
      // that race can end up reporting a vaguer reason (a getIdToken that threw
      // once the winner signed the user out). Without this the specific "your
      // workspace moved to X" would lose to a generic message on timing alone.
      // A generic later error still cannot overwrite anything — it has no
      // destination — so the StrictMode guard above is untouched.
      if (error.destination && !prev.destination) return error;
      return prev;
    });
  }, []);

  /**
   * Clear the failure. Deliberately NOT called at the top of
   * `ensureBackendSession`: StrictMode mounts the listener effect twice, so run
   * #2 would wipe the message run #1 just set and we'd be silent again. Only an
   * explicit new sign-in action (this function) or a sign-in that succeeds may
   * clear it.
   */
  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleProactiveRefresh = useCallback(() => {
    clearRefreshTimer();
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });
        if (res.ok) {
          const data: AuthUser = await res.json();
          setUser(data);
          // Schedule next refresh
          scheduleProactiveRefresh();
        }
      } catch {
        // Silent fail — 401 interceptor in client.ts will handle it
      }
    }, PROACTIVE_REFRESH_MS);
  }, [clearRefreshTimer]);

  const fetchMe = useCallback(async (): Promise<AuthUser | null> => {
    if (inflightMe.current) return inflightMe.current;

    inflightMe.current = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) {
          // If 401, try refresh before giving up
          if (res.status === 401) {
            const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
              method: "POST",
              credentials: "include",
            });
            if (refreshRes.ok) {
              return (await refreshRes.json()) as AuthUser;
            }
          }
          return null;
        }
        return (await res.json()) as AuthUser;
      } catch {
        return null;
      } finally {
        inflightMe.current = null;
      }
    })();

    return inflightMe.current;
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await fetchMe();
    // A session that actually came back is a sign-in that succeeded — one of the
    // two sanctioned moments to drop the failure. A null result is not: it is
    // not a sign-in attempt, so it neither sets nor clears anything.
    if (me) setAuthError(null);
    setUser(me);
  }, [fetchMe]);

  const schedulePrewarm = useCallback((sessionUser: AuthUser | null) => {
    // Phase 7 login prewarm. Runs once per account-session in a background
    // idle callback so the login path is never blocked. SUPPORT is a
    // temporary internal access role; no reason to heat the cache for them.
    if (!sessionUser?.activeAccount) return;
    if (sessionUser.activeAccount.role === "SUPPORT") return;
    const accountId = sessionUser.activeAccount.id;
    const run = () => {
      import("@/services/reportPrewarmWorker")
        .then((m) => m.runPrewarmOncePerSession(accountId))
        .catch(() => {
          /* module load failure — fire-and-forget */
        });
    };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      // Safari fallback.
      setTimeout(run, 500);
    }
  }, []);

  const ensureBackendSession = useCallback(
    async (firebaseUser: any) => {
      // 1) probeer eerst cookie-sessie
      const me = await fetchMe();
      if (me) {
        setAuthError(null);
        setUser(me);
        scheduleProactiveRefresh();
        schedulePrewarm(me);
        return;
      }

      // 2) geen sessie → login via firebase token (NIET force refresh)
      const idToken = await getIdToken(firebaseUser, false);

      const loginRes = await fetch(`${API_BASE}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ idToken }),
      });

      if (loginRes.ok) {
        const data: AuthUser = await loginRes.json();
        setAuthError(null);
        setUser(data);
        // A fresh sign-in replaced the cookie every other tab shares. Guarded:
        // a signal to other tabs must never break the sign-in itself.
        if (data.activeAccount?.id) {
          postSessionEvent({
            type: "signed-in",
            userPublicId: data.id,
            accountPublicId: data.activeAccount.id,
          });
        }
        scheduleProactiveRefresh();
        schedulePrewarm(data);
        return;
      }

      setUser(null);

      if (loginRes.status === 401 || loginRes.status === 403) {
        // The identity was verified but is not authorized on this host, so
        // retrying with the same Firebase session can only fail again. Evict it,
        // otherwise onAuthStateChanged replays this exact failed login on every
        // page load — forever, and until now silently.
        //
        // On Verity's own front door the BFF may add `code: "ACCOUNT_MOVED"`
        // plus one destination when this account was reassigned to a partner.
        // A body we cannot read is not a failure: fall through to the generic
        // message rather than to silence.
        let destination: PartnerAccountLink | undefined;
        try {
          destination = readMovedDestination(await loginRes.json());
        } catch {
          /* not JSON / already consumed — generic message it is */
        }
        reportAuthError({ kind: "not_authorized", destination });
        try {
          await signOut(getFirebaseAuth());
        } catch {
          // Already signed out / SDK not initialised — the error state is what
          // matters here, and it is already set.
        }
        return;
      }

      // Anything else (5xx, gateway hiccup): say so, but do NOT evict the
      // session. A backend blip must never log a legitimate user out.
      reportAuthError({ kind: "unknown" });
    },
    [fetchMe, scheduleProactiveRefresh, schedulePrewarm, reportAuthError]
  );

  const switchAccount = async (
    accountPublicId: string,
    options: { navigate?: boolean } = {},
  ): Promise<boolean> => {
    // A page with unsaved changes asks first, BEFORE the POST (ADR-0386): the
    // router cannot block the navigation below, because by then the cookie is
    // already on the new account. `navigate: false` is the mismatch notice's
    // switch-back, where the page's transport is stopped and nothing can be
    // saved any more. `false` here means the customer chose to stay.
    if (options.navigate !== false && !(await confirmDiscardUnsaved())) return false;
    setSwitching(true);
    try {
      const data = await apiJson<AuthUser>("/auth/switch-account", {
        method: "POST",
        body: JSON.stringify({ accountPublicId }),
      });
      // Re-scope (and thereby clear) the segment cache BEFORE the new account
      // renders, so no new-account component can read the previous account's
      // cached schema/values. Synchronous + ordered — closes the switch race.
      setAccountScope(data.activeAccount.id);
      // On a tenant page, move to the same SECTION of the new account
      // (ADR-0302, Decision 5): never to an object of the previous account,
      // and never one render that pairs the URL account with another session
      // account (TenantScope would stop the transport and abort the requests
      // in flight, in the tab that switched on purpose).
      //
      // That takes two things. First, `navigate` is a synchronous history
      // push, so every request from here on names the new account; that holds
      // only while the app router's one route has no loader, middleware or
      // `getContext` (routing/appRouter.ts, ADR-0386), pinned by
      // `routing/__tests__/appRouter.test.tsx`. Second, the new location and
      // the new session land in ONE render. They sit on different React
      // lanes: RouterProvider applies a location inside `startTransition`
      // unless the update asks for `flushSync` (react-router 7.12.0
      // `chunk-EPOLDU6W.mjs:6326-6337`), while `setUser` writes the session
      // store, a `useSyncExternalStore` that always renders on the Sync lane.
      // So both go inside one `flushSync`: `setUser` first, then the
      // navigate with `flushSync: true`, whose own flush renders them
      // together. Measured: without the batch, `[URL A, session B]`; with
      // only the navigate flushed, `[URL B, session A]`.
      //
      // Switching back from the notice passes `navigate: false`: the URL
      // already names that account, so `setUser` alone is the agreement.
      const currentPath = window.location.pathname;
      const navigate = navigateRef.current;
      if (options.navigate !== false && tabAccountFromPath(currentPath) && navigate) {
        const root = sectionRoot(stripTenantPrefix(currentPath));
        flushSync(() => {
          setUser(data);
          void navigate(`/a/${data.activeAccount.id}${root === "/" ? "" : root}`, {
            flushSync: true,
          });
        });
      } else {
        setUser(data);
      }
      // The cookie every other tab shares is on the new account now.
      postSessionEvent({ type: "account-switched", accountPublicId: data.activeAccount.id });
      scheduleProactiveRefresh();
      // Phase 7: clear per-account prewarm dedup so the new account gets
      // prewarmed on its next mount. Same fire-and-forget dynamic-import
      // pattern as segmentCache above.
      import("@/services/reportPrewarmWorker")
        .then((m) => {
          m.clearPrewarmDedup();
          // Fire prewarm for the newly-active account immediately — we're
          // already past the blocking part of login here.
          m.runPrewarmOncePerSession(data.activeAccount.id).catch(() => {
            /* fire-and-forget */
          });
        })
        .catch(() => {
          /* module load failure — next mount picks it up */
        });
      return true;
    } catch (error) {
      console.error("Fout bij het wisselen van account:", error);
      return false;
    } finally {
      setSwitching(false);
    }
  };

  // Another tab changed the session (ADR-0302). Record it; the mismatch gate
  // decides what this tab shows. A signal only: the BFF's check is what
  // refuses a stale tab's requests, message or not.
  useEffect(
    () =>
      subscribeSessionEvents((event) => {
        if (event.type === "account-switched") {
          reportSessionAccount(event.accountPublicId);
        } else if (event.type === "signed-out") {
          reportSessionNotice("signed-out");
        } else if (userRef.current && event.userPublicId !== userRef.current.id) {
          reportSessionNotice("other-user");
        } else {
          reportSessionAccount(event.accountPublicId);
        }
      }),
    [],
  );

  const logout = useCallback(async () => {
    clearRefreshTimer();
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Best-effort — cookies may already be cleared
    }
    postSessionEvent({ type: "signed-out" });
    try {
      await signOut(getFirebaseAuth());
    } catch {
      // Firebase sign-out may fail if already signed out / not yet initialised
    }
    setUser(null);
    // Explicit user action: the login screen they land on must be clean.
    setAuthError(null);
  }, [clearRefreshTimer]);

  useEffect(() => {
    // Wait until Firebase is initialised for the resolved partner project.
    if (!firebaseReady) return;
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), async (firebaseUser) => {
      setLoading(true);
      try {
        if (!firebaseUser) {
          // Reached (among others) by the sign-out we fire on a 401/403. It must
          // touch the session only — wiping authError here would erase the
          // message we just set, one tick after setting it.
          clearRefreshTimer();
          setUser(null);
          return;
        }
        await ensureBackendSession(firebaseUser);
      } catch (e) {
        // A genuine network failure never reaches the status check above: fetch
        // REJECTS, and the rejection lands here. So does a getIdToken that fails
        // because the user was signed out mid-flight. Report it — but never sign
        // out, and never overwrite a status-derived reason (reportAuthError
        // keeps the first one).
        console.error("Auth bootstrap failed:", e);
        reportAuthError({ kind: "network" });
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      clearRefreshTimer();
    };
  }, [firebaseReady, ensureBackendSession, clearRefreshTimer, reportAuthError]);

  const activeAccount = user ? user.activeAccount : null;
  const allAccounts = user ? user.accounts : [];
  // Build rights on the active membership: the BFF ships them on every auth
  // response (empty for SUPPORT, all for a superadmin). Buttons only — the
  // backend re-reads the grant per request.
  const capabilities = useMemo(
    () => activeAccount?.capabilities ?? [],
    [activeAccount]
  );
  const hasCapability = useCallback(
    (capability: Capability) => capabilities.includes(capability),
    [capabilities]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        switching,
        setUser,
        refreshUser,
        switchAccount,
        logout,
        authError,
        clearAuthError,
        reportAuthError,
        activeAccount,
        allAccounts,
        capabilities,
        hasCapability,
      }}
    >
      {inRouter && <NavigatorBridge target={navigateRef} />}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
